import React from 'react';
import { Home, Calendar, User, LogOut } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User as UserType } from '../types/user';

interface LayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
  user: UserType;
}

const Layout = ({ children, onLogout, user }: LayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    {
      icon: <Home size={24} />,
      label: 'Accueil',
      path: '/home',
    },
    {
      icon: <Calendar size={24} />,
      label: 'Rendez-vous',
      path: '/appointments',
    },
    {
      icon: <User size={24} />,
      label: 'Profil',
      path: '/profile',
    },
  ];

  const handleLogoClick = () => {
    // Navigate to the landing page and trigger logout
    onLogout()
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Shows user name on mobile */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b md:hidden z-10 px-4">
        <div className="h-full flex items-center justify-between">
          <button onClick={handleLogoClick}>
            <img 
              src="/yapadra-logo.png"
              alt="Yapadra Health"
              className="h-8"
            />
          </button>
          <button
          onClick={onLogout}
          className="flex items-center gap-2 text-gray-500 hover:text-red-500 transition-colors"
        >
          <LogOut size={20} />
          <span className="text-sm">Déconnexion</span>
        </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-14 pb-16 md:pt-0 md:pl-[80px] animate-in fade-in duration-300">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t md:hidden">
        <div className="flex justify-around items-center h-16">
          {navigationItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center w-full h-full ${
                location.pathname === item.path
                  ? 'text-[#00c4cc]'
                  : 'text-gray-500'
              }`}
            >
              {item.icon}
              <span className="text-xs mt-1">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Desktop Sidebar */}
      <nav className="fixed left-0 top-0 bottom-0 hidden md:flex flex-col min-w-[80px] w-auto bg-white border-r">
        <button 
          onClick={handleLogoClick}
          className="p-3 hover:opacity-80 transition-opacity flex justify-center"
        >
          <img 
            src="/yapadra-logo.png"
            alt="Yapadra Health"
            className="w-10"
          />
        </button>
        
        <div className="flex flex-col items-center flex-1 py-4">
          {navigationItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center w-full px-3 py-2 mb-2 transition-colors ${
                location.pathname === item.path
                  ? 'text-[#00c4cc]'
                  : 'text-gray-500 hover:text-[#00c4cc]'
              }`}
            >
              {item.icon}
              <span className="text-xs mt-1 whitespace-nowrap">{item.label}</span>
            </button>
          ))}
        </div>
        
        {/* Logout Button */}
        <button
          onClick={onLogout}
          className="flex flex-col items-center justify-center w-full px-3 py-2 text-gray-500 hover:text-red-500 transition-colors"
        >
          <LogOut size={24} />
          <span className="text-xs mt-1 whitespace-nowrap">Déconnexion</span>
        </button>
      </nav>
    </div>
  );
};

export { Layout };